import { createSignal } from "solid-js";
import type { ScrollbackMessage } from "./api";
import type { ChannelKey } from "./channelKey";
import { moduleRoot } from "./moduleRoot";
import type { ScrollbackMetaTKey } from "./wireTypes";

// #222 — hide join/part/quit/nick-change signalling (and, since #1262,
// channel mode churn) on large channels by default, with a per-channel opt-in
// to re-show. Closed-set per-channel
// preference with localStorage persistence, backed by a module-singleton
// Solid signal so every open scrollback pane re-filters the moment the
// operator toggles the per-channel control.
//
// ## The whole feature in one sentence
//
// grappa STILL delivers every suppressed kind over the wire (no wire change,
// no server change); cic decides whether to RENDER them. The render decision
// is: on a "large" channel these kinds are pure noise, so hide them by
// default — but let the operator pin a per-channel choice that WINS over the
// size default.
//
// ## Why a render-layer filter, not a store drop
//
// Suppression is purely visual. The message store (`scrollback.ts`) stays
// intact so unread-counting, the read-cursor divider, and own-JOIN
// auto-focus (all of which read `messages()`, not the rendered rows) keep
// working. ScrollbackPane's `rows()` memo is the filter site.
//
// ## Why closed-set keys, not a boolean-per-channel
//
// A boolean can't express "no explicit choice — follow the size default".
// The precedence rule the issue flagged as "going to be tough" needs a
// TRI-STATE: "show" | "hide" | unset (key absent). Explicit choice wins;
// unset follows the live member-count default. A bare boolean would collapse
// unset into one of the two poles and lose the auto-hide-on-growth behaviour.
// Closed-set union per CLAUDE.md ("atoms/literals, never untyped strings").
//
// ## Why a signal (mirror of timeFormat.ts)
//
// The pref is consumed at render time by ScrollbackPane's `rows()` memo. A
// bare `localStorage.getItem` there would NOT re-run when the toggle flips.
// Backing the per-channel map with a signal (createRoot, module-singleton)
// makes `channelPresenceVisible` reactive: reading it inside the memo tracks
// the signal, so toggling live re-filters the pane.
//
// ## Server-backed since #449 (localStorage is now the offline/write-through cache)
//
// This pref is one of the three server-backed display prefs (with #217 time
// format + #443 colored nicklist), coordinated by `displayPrefs.ts` over
// `PUT/GET /me/settings/display-prefs` so a single account converges its UI
// across devices (the reporter's desktop toggle was invisible on the iOS PWA).
// localStorage is no longer the source of truth — it is the boot/offline cache
// that gives a FOUC-free first paint and survives an offline session; the
// server wins on login (or is seeded up once when it has never persisted).
// `setChannelPresencePref` / `clearChannelPresencePref` stay LOCAL-only (the
// coordinator's `syncedSetChannelPresencePref` adds the PUT); `getAllPresencePrefs`
// + `replacePresencePrefs` are the coordinator's whole-map read/apply seams.
// (An earlier moduledoc cited `feedback_no_localized_strings_server_side` for
// keeping this client-only — that was a mis-application: that memory is about
// i18n, not display-pref persistence.)

// "large" cutoff. Named constant, one-line tune. 200+ member channels drown
// in J/P/Q; smaller channels keep them visible. A channel whose live member
// count crosses this while the pref is unset auto-flips. Raised from 50 to
// 200 in #915: a 50-member channel does not drown, and auto-denoising it hid
// traffic the operator wanted. MUST equal the server twin
// `Grappa.PresenceFilter`'s `@large_channel_threshold` (lib/grappa/
// presence_filter.ex) — the server applies the SAME size default to the REST
// history fetch (#458), so a divergence makes page-up disagree with the live
// tail on any channel sized between the two values. No gate pins them; the
// two moduledocs cross-reference and that is the whole guard.
export const LARGE_CHANNEL_THRESHOLD = 200;

// The noise set — join/part/quit/nick_change/mode. Still NOT ScrollbackPane's
// `PRESENCE_KINDS` (which also holds topic/kick/server_event): those three are
// not churn and stay visible.
//
// `mode` joined the set in #1262. It was previously carved OUT by #458 on the
// rule "mode carries operator-relevant signal and MUST stay visible"; vjt
// withdrew that rule on 2026-08-13. Its accepted cost — a denoised channel
// folding the structural transitions (`+b` / `+k` / `+l` / `+m` / `+i`) along
// with the status-prefix churn it was aimed at — came due on issue 2176, and
// is paid off NOT by taking the kind back out but by the per-row exemption
// `STRUCTURAL_META_KEY` below. `mode` stays here: a `+o` is still churn.
//
// Own-nick mode rows (#154(b): `/umode +i`, the services-pushed `+r`/`+a` at
// IDENTIFY) are NOT affected — they land on the synthetic `$server` window,
// which has no member count, so the server resolves it to SHOW.
//
// MUST equal the server twin `Grappa.Scrollback.Message.suppressed_presence_kinds/0`
// (lib/grappa/scrollback/message.ex), SAME ORDER. Unlike the threshold, this one
// IS gated: `test/grappa/presence_filter_test.exs` parses this literal and fails
// on any drift.
export const SUPPRESSED_PRESENCE_KINDS: ReadonlySet<ScrollbackMessage["kind"]> = new Set([
  "join",
  "part",
  "quit",
  "nick_change",
  "mode",
]);

// issue 2176 — the per-ROW escape hatch out of the set above. The server tags
// a `:mode` row `meta.structural = true` when its token changed the CHANNEL (a
// ban, a key, a limit, a flag) rather than a member's status prefix; a tagged
// row renders even while the channel is denoised. Everything else about the
// filter is unchanged.
//
// WHY A TAG AND NOT A PARSE HERE: the decision needs the mode LETTERS, and the
// churn letters are per-network (`PREFIX=`). Parsing `meta.modes` on both sides
// would be two copies of one parser, which is the drift this module's gate
// exists to catch — and that gate compares sets of KINDS, so it would not
// catch it. The server parses once, where the network's ISUPPORT already is,
// and both sides read the tag. cic NEVER parses IRC (CLAUDE.md invariant).
//
// ABSENCE MEANS FOLD, and that is load-bearing rather than incidental: a `+o`
// row and a row written before the tag existed both arrive without the key, and
// both must keep behaving exactly as they did. There is no `false` form to
// check for.
//
// Typed `satisfies ScrollbackMetaTKey` so the key is checked at COMPILE time
// against the server's generated meta allowlist — if the server ever drops it,
// tsc says so here. The runtime spelling is additionally gated against
// `Grappa.Scrollback.Message.structural_meta_key/0` by
// `test/grappa/presence_filter_test.exs`, which parses this literal.
export const STRUCTURAL_META_KEY = "structural" satisfies ScrollbackMetaTKey;

// The row shape the presence predicate needs: the kind, and the meta that may
// carry the structural tag. `meta` is optional because callers that build a
// synthetic row (and the `trailingHiddenAdvanceTarget` element type) have no
// reason to carry one — an absent meta is the untagged case, which folds.
export type PresenceRow = {
  readonly kind: ScrollbackMessage["kind"];
  readonly meta?: ScrollbackMessage["meta"];
};

// The one place the tag is read. Strict `=== true`: `meta` values are `unknown`
// on the generated type, and a truthy-check would promote a stray string into
// "always visible".
export function isStructuralRow(row: PresenceRow): boolean {
  return row.meta?.[STRUCTURAL_META_KEY] === true;
}

// unset (key absent) = follow the size default.
export type PresencePref = "show" | "hide";

const STORAGE_KEY = "cicchetto.presenceFilter";

type PrefMap = Record<ChannelKey, PresencePref>;

function isPresencePref(v: unknown): v is PresencePref {
  return v === "show" || v === "hide";
}

// Read the persisted per-channel map, dropping any corrupt / unknown entries
// so one bad key can't poison the whole store. A parse failure (corrupt JSON)
// yields the empty map — every channel then follows the size default.
function readStored(): PrefMap {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return {};
    const out: PrefMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (isPresencePref(v)) out[k as ChannelKey] = v;
    }
    return out;
  } catch {
    return {};
  }
}

// Module-singleton signal seeded from storage. createRoot anchors it for the
// app lifetime (same shape as timeFormat.ts) — the preference is identity-
// agnostic display state, so no token-rotation reset arm is needed.
const { prefs, setPrefs } = moduleRoot(() => {
  const [prefs, setPrefs] = createSignal<PrefMap>(readStored());
  return { prefs, setPrefs };
});

function persist(map: PrefMap): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

// Non-reactive read for the pure precedence table + tests. Callers in a
// reactive render path use `channelPresenceVisible` so rows() re-filters.
export function getChannelPresencePref(key: ChannelKey): PresencePref | undefined {
  return prefs()[key];
}

export function setChannelPresencePref(key: ChannelKey, pref: PresencePref): void {
  setPrefs((prev) => {
    const next = { ...prev, [key]: pref };
    persist(next);
    return next;
  });
}

// Back to follow-the-size-default: remove the explicit pin entirely. This is
// the tri-state's API-completing "return to unset" operation (set/get/clear
// over the three states show/hide/unset). No UI affordance wires it today —
// the toggle is binary show↔hide per the #222 spec — but it is unit-tested and
// is the reset seam the ScrollbackPane wiring test uses between cases (same
// role as `clearReadCursors` / `seedFromTest` elsewhere). Kept deliberately so
// a future "reset to auto" affordance has a first-class verb instead of
// reaching into localStorage.
export function clearChannelPresencePref(key: ChannelKey): void {
  setPrefs((prev) => {
    if (!(key in prev)) return prev;
    const next = { ...prev };
    delete next[key];
    persist(next);
    return next;
  });
}

// #449 — whole-map snapshot for the display-prefs coordinator's `buildWireMap`.
// A shallow copy so a caller can't mutate the signal's backing map. Keys are
// the opaque `ChannelKey` strings the server round-trips verbatim.
export function getAllPresencePrefs(): Record<string, PresencePref> {
  return { ...prefs() };
}

// #449 — whole-map local apply for the coordinator (`applyServerPrefs` on a
// server-wins reconcile). Full replace, NOT merge: a channel absent from the
// incoming map returns to unset (tri-state absence preserved end to end), so
// the local map exactly mirrors the server's pinned set. Write-through to the
// signal + localStorage; NO server PUT (the coordinator owns the round-trip).
// Accepts plain string keys — the server sends `ChannelKey`-shaped strings it
// built from `channelKey(slug, name)`; the branding is a client-only refinement.
export function replacePresencePrefs(map: Record<string, PresencePref>): void {
  const next = { ...map } as PrefMap;
  persist(next);
  setPrefs(next);
}

// THE PRECEDENCE RULE (the "tough" part). Pure + testable without the signal
// (mirror of timeFormat.ts's `renderTimestamp` vs `formatTimestamp`). An
// explicit choice WINS over the size default; unset follows the live count.
export function resolvePresenceVisible(
  pref: PresencePref | undefined,
  memberCount: number,
): boolean {
  if (pref === "show") return true; // explicit override wins
  if (pref === "hide") return false; // explicit override wins
  return memberCount < LARGE_CHANNEL_THRESHOLD; // unset → follow size default
}

// Reactive wrapper: reading `prefs()` inside a SolidJS render tracks the
// signal, so ScrollbackPane's rows() memo re-filters live when the toggle
// flips. Pass the CURRENT member count (from `membersByChannel()`) so it also
// re-runs when membership crosses the threshold.
export function channelPresenceVisible(key: ChannelKey, memberCount: number): boolean {
  return resolvePresenceVisible(prefs()[key], memberCount);
}

// #239 — the SINGLE "is this row visible under the channel's presence filter?"
// predicate. BOTH the render filter (ScrollbackPane's `rows()` memo) AND the
// unread-count derivation (selection.ts `perChannelUnread`) route through it,
// so a hidden control row can never inflate a badge the operator cannot clear
// (the count and the pane must agree on which rows "count" — CLAUDE.md "one
// feature, one code path"). A row is visible unless the channel is suppressing
// presence AND the row is one of the noise kinds. Reads the reactive
// pref signal (via `channelPresenceVisible`) ONLY for a suppressed kind, so the
// consumer memo/effect re-runs on toggle / membership-threshold changes exactly
// when a suppressed row is present. Never fold a second copy of this rule.
//
// issue 2176 — takes the ROW, not just its kind, because the suppressed set is
// no longer the whole answer for `mode`: a row the server tagged structural is
// exempt. The tag check sits BEFORE the pref read on purpose — a structural row
// is visible whatever the channel's preference says, so reading the signal for
// it would subscribe the consumer memo to a pref that cannot change the answer.
export function presenceRowVisible(
  key: ChannelKey,
  memberCount: number,
  row: PresenceRow,
): boolean {
  if (!SUPPRESSED_PRESENCE_KINDS.has(row.kind)) return true;
  if (isStructuralRow(row)) return true;
  return channelPresenceVisible(key, memberCount);
}

// #239 — the read-cursor advance target that skips the TRAILING run of hidden
// control messages on window display, WITHOUT marking any VISIBLE unread read.
// The presence filter hides join/part/quit/nick_change/mode; the DOM-geometry
// settle paths (scroll / leave / blur) only ever reach the last RENDERED row,
// so a trailing run of hidden control messages past the cursor never gets a
// settle event and `last_read_message_id` stays stuck below them — the stuck
// badge.
//
// Order-INDEPENDENT: works on `msgs` in any order (the store sorts by
// [server_time asc, id asc], NOT by id — a delayed/batched or clock-skewed row
// can carry an earlier server_time yet a higher id, so array order can diverge
// from id order). We reason purely in id space: the target is the highest
// HIDDEN unread id that is strictly BELOW the lowest VISIBLE unread id (the
// ceiling we must never cross — advancing to/past it would mark real content
// read the operator never saw). When the whole post-cursor tail is hidden there
// is no ceiling, so the target is the tail id. Returns `cursor` when nothing is
// skippable, so the caller's forward-only `setCursorIfAdvances` (#233 monotonic
// clamp) makes it a no-op. Pure (predicate injected) — unit-testable without
// DOM/timers; the caller injects `presenceRowVisible(key, memberCount, row)`.
//
// issue 2176 — the injected predicate takes the whole ROW rather than its kind,
// for the same reason `presenceRowVisible` does: a structural mode row is
// VISIBLE, so it is a ceiling, and skipping past it would mark a ban read that
// the operator never saw.
export function trailingHiddenAdvanceTarget(
  msgs: readonly ({ readonly id: number } & PresenceRow)[],
  cursor: number,
  isVisible: (row: PresenceRow) => boolean,
): number {
  // Lowest-id VISIBLE unread — the ceiling. +Infinity when the whole
  // post-cursor tail is hidden (no visible unread to protect).
  let ceiling = Number.POSITIVE_INFINITY;
  for (const m of msgs) {
    if (m.id > cursor && isVisible(m) && m.id < ceiling) ceiling = m.id;
  }
  // Highest HIDDEN unread id strictly below the ceiling — the skippable run.
  let target = cursor;
  for (const m of msgs) {
    if (m.id > cursor && !isVisible(m) && m.id < ceiling && m.id > target) {
      target = m.id;
    }
  }
  return target;
}
